import * as React from 'react';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import * as moment from 'moment';
import { find } from 'lodash';
import { Tag, Tooltip, DateTime, Notification, Icon, Pagination } from '../';
import { getPayments } from '../../redux/actions/payments';
import PAGINATE from '../../data/paginate';
import Loader from '../Loader/Loader';
import Box from '../Box/Box';

interface Props {
  vehicleTypes: any;
  dispatch: Function;
  isArchived: boolean;
  bankingExportId: number;
  bookingId?: number;
  action?: Function;
  id?: number;
  all?: boolean;
  hideNotes?: boolean;
}

interface State {
  isLoading: boolean;
  bookingId: number;
  page: number;
  sort: 'ASC' | 'DESC';
  payments: Array<any>;
  order?: string;
}

class PaymentTable extends React.Component<Props, State> {
  static defaultProps = {
    all: false,
    hideNotes: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      bookingId: this.props.bookingId,
      page: 0,
      sort: 'DESC',
      payments: [],
      order: 'created_at',
    };
    this.handleUpdate = this.handleUpdate.bind(this);
    this.renderItem = this.renderItem.bind(this);
  }

  /**
   * Load vehicle types from API when isArchived prop updated
   */
  componentWillReceiveProps(newProps: Props) {
    if (this.props.isArchived !== newProps.isArchived) {
      this.setState({ isLoading: true }, () =>
        this.handleUpdate(newProps.isArchived),
      );
    }
  }

  /**
   * Load vehicle types and payments from API
   */
  async componentDidMount() {
    this.handleUpdate();
  }

  /**
   * Update the list of payments
   */
  async handleUpdate(isArchived = this.props.isArchived) {
    let response;
    const requestPayments = this.props.action ? this.props.action : getPayments;
    response = await this.props.dispatch(
      requestPayments({
        isArchived,
        id: this.props.id,
        page: this.state.page,
        booking_id: this.state.bookingId,
        order: this.state.order,
        sort: this.state.sort,
        all: this.props.all,
        banking_export_id: this.props.bankingExportId,
      }),
    );
    const payments = [];
    await Promise.all(
      response.data.map(async (payment) => {
        if (payment.booking) {
          payment.vehicleType = find(this.props.vehicleTypes, {
            id: payment.booking.vehicle_type_id,
          });
          payment.duration =
            1 +
            moment(payment.booking.end).diff(
              moment(payment.booking.start),
              'd',
            );
        }
        payments.push(payment);
      }),
    );
    this.setState({ payments, isLoading: false });
  }

  /**
   * Render an individual payment item
   * @param {Object} payment
   */
  renderItem(payment) {
    let typeColour = 'primary';
    switch (payment.type) {
      case 'ACCOUNT':
        typeColour = 'success';
        break;
      case 'CREDIT-NOTE':
        typeColour = 'danger';
        break;
      case 'PRE-AUTH':
        typeColour = 'warning';
        break;
    }
    if (payment.origin === 'SAGEPAY') {
      typeColour = 'info';
    }

    const tooltip = `${payment.vehicleType.name} booking for ${
      payment.duration
    } day${payment.duration !== 1 ? 's' : ''}, from ${moment(
      payment.booking.start,
    ).format('DD/MM/YY')} to ${moment(payment.booking.end).format('DD/MM/YY')}`;

    return (
      <tr
        key={payment.id}
        onClick={() => this.props.dispatch(push(`/payments/${payment.id}`))}
      >
        {!this.props.bookingId && (
          <td>
            <div className="media">
              <div className="media-left">
                <Tag type="dark">{payment.booking.invoiceId}</Tag>
              </div>
              <div className="media-content">
                {payment.booking.contact_name || 'Unknown Contact'}
                <Tooltip text={tooltip}>
                  <Icon icon="info-circle has-text-grey-light" />
                </Tooltip>
              </div>
            </div>
          </td>
        )}
        <td>£{Number(payment.total).toFixed(2)}</td>
        <td>
          <DateTime date={payment.created_at} />
        </td>
        <td>
          {payment.type && (
            <Tag type={typeColour}>
              {payment.type}
              {payment.origin === 'SAGEPAY' ? ' (ONLINE)' : ''}
            </Tag>
          )}
        </td>
        {!this.props.hideNotes && (
          <td>
            {!!payment.notes &&
              !!payment.notes.length &&
              payment.notes.map((note) => (
                <div key={`note-${note.id}`}>
                  <small className="media">
                    <div className="media-left">
                      <Icon icon="sticky-note has-text-grey-light" />
                    </div>
                    <div className="media-content">{note.text}</div>
                  </small>
                </div>
              ))}
          </td>
        )}
      </tr>
    );
  }

  render() {
    const headings = [
      ...(this.props.bookingId
        ? []
        : [{ title: 'Booking', value: 'booking_id', sortable: true }]),
      { title: 'Amount', value: 'total', sortable: true },
      { title: 'Date', value: 'created_at', sortable: true },
      { title: 'Type', value: 'type', sortable: true },
    ];

    if (!this.props.hideNotes) {
      headings.push({ title: 'Notes', value: 'notes', sortable: false });
    }

    if (this.state.isLoading) {
      return <Loader fullwidth />;
    }

    return (
      <div className="payment-table">
        {this.state.payments.length === 0 ? (
          <Notification type="default">No payments could be found</Notification>
        ) : (
          <>
            <Box>
              <table className="is-striped">
                <thead>
                  <tr>
                    {headings.map((heading) => (
                      <th key={heading.value}>
                        {heading.sortable ? (
                          <a
                            className={
                              this.state.order === heading.value
                                ? 'is-active'
                                : 'has-text-grey'
                            }
                            onClick={() => {
                              const newState: any = {
                                order: heading.value,
                                sort: 'ASC',
                                isLoading: true,
                              };
                              if (this.state.order === heading.value) {
                                newState.sort =
                                  this.state.sort === 'ASC' ? 'DESC' : 'ASC';
                              }
                              this.setState(newState, this.handleUpdate);
                            }}
                          >
                            <div className="level is-mobile">
                              <div className="level-left">
                                <div className="level-item">
                                  <small>{heading.title}</small>
                                </div>
                              </div>
                              <div className="level-right">
                                <div className="level-item">
                                  <Icon
                                    size="small"
                                    icon={`sort-amount-${
                                      this.state.order === heading.value &&
                                      this.state.sort === 'DESC'
                                        ? 'desc'
                                        : 'asc'
                                    }`}
                                  />
                                </div>
                              </div>
                            </div>
                          </a>
                        ) : (
                          <small>{heading.title}</small>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {this.state.payments.map((payment, index) => {
                    if (index >= PAGINATE.payments.MAX) {
                      return null;
                    }
                    return this.renderItem(payment);
                  })}
                </tbody>
              </table>
            </Box>
            <Pagination
              page={this.state.page}
              lastPage={this.state.payments.length <= PAGINATE.payments.MAX}
              handleUpdate={(page) => {
                this.setState({ page, isLoading: true }, () => {
                  this.handleUpdate();
                });
              }}
            />
          </>
        )}
      </div>
    );
  }
}

const mapStateToProps = ({ vehicleTypes }) => ({
  vehicleTypes: vehicleTypes.vehicleTypes,
});

export default connect(mapStateToProps)(PaymentTable);
